import type { FC } from 'react';
import React, { Fragment, useRef } from 'react';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { token } from '@atlaskit/tokens';
import { N20 } from '@atlaskit/theme/colors';

import {
	HOME_RIGHT_PANEL_WELCOME_MESSAGE_EXPERIENCE,
	HOME_RIGHT_PANEL_APPS_EXPERIENCE,
	HOME_SPACES_EXPERIENCE,
	HOME_RIGHT_PANEL_EXPERIENCE,
	ExperienceStart,
	collectAll,
} from '@confluence/experience-tracker';
import { ErrorDisplay } from '@confluence/error-boundary';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics';

import { HOME_OVERVIEW_RIGHT_PANEL_PAGE_SEGMENT } from '../../perf.config';
import { useHomeWidgetState } from '../useHomeWidgetState';

import { ForgeApps } from './ForgeApps';
import { RightPanelDragHandle } from './RightPanelDragHandle';
import { useRightPanelVisible } from './useRightPanelVisible';
import { useRightPanelResize } from './useRightPanelResize';

const DEFAULT_WIDTH = 475;
const MIN_WIDTH = 350;
const MAX_WIDTH = 818;
const POPULAR_COMPACT_THRESHOLD = 500;

type WidthMode = 'resizable' | 'fullwidth';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const ResizablePanelContainer = styled.aside`
	box-sizing: border-box;
	height: 100%;
	overflow-y: auto;
	word-break: break-word;
	background-color: ${token('elevation.surface.raised', N20)};
	padding: ${token('space.150', '12px')};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const PanelContainer = styled.aside`
	box-sizing: border-box;
	height: calc(100% - 12px);
	word-break: break-word;
	margin-bottom: ${token('space.300', '24px')};
`;

export type RightPanelProps = {
	widthMode?: WidthMode;
};

const collectRightPanelExperiences = (events, experience) => {
	collectAll([
		HOME_RIGHT_PANEL_WELCOME_MESSAGE_EXPERIENCE,
		HOME_RIGHT_PANEL_APPS_EXPERIENCE,
		HOME_SPACES_EXPERIENCE,
	])(events, experience);
};

export const RightPanel: FC<RightPanelProps> = ({ widthMode = 'resizable' }) => {
	const rightPanelRef = useRef<HTMLDivElement | null>(null);
	const { shouldShowForgeApps } = useRightPanelVisible();

	const { onResizeDrag, onResizeComplete, initialWidth } = useRightPanelResize({
		rightPanelRef,
		defaultWidth: DEFAULT_WIDTH,
		minWidth: MIN_WIDTH,
		maxWidth: MAX_WIDTH,
		compactThreshold: POPULAR_COMPACT_THRESHOLD,
	});

	// Query for widget state here so that the data is ready by the time the CollapsibleSections render
	const { error, loading: preferencesLoading } = useHomeWidgetState('right-panel');

	const OuterContainer = useRightPanelContainer(widthMode);
	const widthStyle = useWidthStyle(widthMode, initialWidth);

	if (preferencesLoading) {
		return null;
	}

	return (
		<OuterContainer
			ref={rightPanelRef}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={widthStyle}
		>
			{widthMode === 'resizable' && (
				<RightPanelDragHandle onResizeDrag={onResizeDrag} onResizeComplete={onResizeComplete} />
			)}
			<ExperienceStart name={HOME_RIGHT_PANEL_EXPERIENCE} collect={collectRightPanelExperiences} />
			<Fragment>
				<div data-testid="right-panel-content">{shouldShowForgeApps && <ForgeApps />}</div>
				{error && <ErrorDisplay error={error} />}
				<PageSegmentLoadEnd metric={HOME_OVERVIEW_RIGHT_PANEL_PAGE_SEGMENT} />
			</Fragment>
		</OuterContainer>
	);
};

const useRightPanelContainer = (widthMode: WidthMode) =>
	widthMode === 'resizable' ? ResizablePanelContainer : PanelContainer;

const useWidthStyle = (widthMode: WidthMode, initialWidth: number) => {
	const width = widthMode === 'fullwidth' ? 'auto' : initialWidth;
	return { width };
};
